import { useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { createRoot } from "react-dom/client";
import type { Block, PrintDoc, PrintImage } from "./render";

// Report printing (correction pass 2026-10-27, item 7; docs/report-printing.md): a report prints from its data, not
// from the screen. The layout is built from the report's columns and every row the filters returned (not the rows
// that happen to be visible), cut into A4 pages here in the browser by measuring each row, so the page breaks, the
// repeated column headings and "Page i / N" are the same in every browser (Chromium and Firefox alike; neither needs
// CSS paged-media support for it). The pages are ordinary HTML text: printed to PDF, the text stays selectable.
// The same data also renders as raster pages for the A4 printer mapped to this PC through the Delta print agent.

export interface ReportPrintCol { label: string; grp?: string; end?: boolean }
export interface ReportPrintRow { cells: string[]; kind?: "open" | "section" | "subtotal" | "total" }
export interface ReportPrintData {
  /** The organization, shown in every page's header. */
  org: string;
  title: string;
  /** Filters and period as applied: label and value. */
  meta: [string, string][];
  cols: ReportPrintCol[];
  rows: ReportPrintRow[];
  foot?: string[];
  /** Notes printed after the table (reconciliation, warnings such as "not reconciled"). */
  notes?: string[];
  printedBy: string;
  printedAt: string;
  lang: "en" | "ar";
  /** Labels in the report's language. */
  text: { page: (i: number, n: number) => string; continued: string; printedBy: string; noRows: string };
}

const MM = 96 / 25.4;
const PAD_MM = 11;

/** Portrait for up to 8 columns, landscape beyond, so no column is squeezed out of the page. */
export const orientation = (d: Pick<ReportPrintData, "cols">) => (d.cols.length > 8 ? "landscape" : "portrait");

function Head({ d, first }: { d: ReportPrintData; first: boolean }) {
  return (
    <header className="rpp-head">
      <div className="rpp-org">
        <img className="rpp-logo" src="/api/organization/logo" alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
        <div><div className="rpp-orgname">{d.org}</div><h1 className="rpp-title">{d.title}{!first && <span className="rpp-cont"> · {d.text.continued}</span>}</h1></div>
      </div>
      {first && d.meta.length > 0 && <dl className="rpp-meta">{d.meta.map(([k, v], i) => <div key={i}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}
    </header>
  );
}

function THead({ cols }: { cols: ReportPrintCol[] }) {
  if (!cols.some((c) => c.grp)) return <tr>{cols.map((c, i) => <th key={i} className={c.end ? "end" : ""}>{c.label}</th>)}</tr>;
  const top: ReactElement[] = [], bot: ReactElement[] = [];
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i]!;
    if (!c.grp) { top.push(<th key={i} rowSpan={2} className={c.end ? "end" : ""}>{c.label}</th>); continue; }
    let n = 1; while (cols[i + n]?.grp === c.grp) n++;
    top.push(<th key={i} colSpan={n} className="grp">{c.grp}</th>);
    for (let j = 0; j < n; j++) bot.push(<th key={i + j} className={cols[i + j]!.end ? "end" : ""}>{cols[i + j]!.label}</th>);
    i += n - 1;
  }
  return <><tr>{top}</tr><tr>{bot}</tr></>;
}

const Row = ({ r, cols }: { r: ReportPrintRow; cols: ReportPrintCol[] }) => (
  <tr className={r.kind ? `rpp-${r.kind}` : ""}>{r.kind === "section"
    ? <td colSpan={cols.length}>{r.cells[0]}</td>
    : cols.map((c, j) => <td key={j} className={c.end ? "end num" : ""}>{r.cells[j] ?? ""}</td>)}</tr>
);

function Foot({ d }: { d: ReportPrintData }) {
  return <>{d.foot && <tfoot><tr>{d.cols.map((c, i) => <td key={i} className={i && c.end ? "end num" : ""}>{d.foot![i] ?? ""}</td>)}</tr></tfoot>}</>;
}

function Notes({ d }: { d: ReportPrintData }) {
  return d.notes?.length ? <div className="rpp-notes">{d.notes.map((n, i) => <p key={i}>{n}</p>)}</div> : null;
}

/** Splits rows into pages by their measured heights; the last page keeps room for the totals and notes. */
export function paginate(h: { first: number; other: number; thead: number; foot: number; notes: number; pageFoot: number; rows: number[] }, pageMm: number): number[][] {
  const content = (pageMm - 2 * PAD_MM) * MM - h.pageFoot - 4;
  const pages: number[][] = [[]];
  let used = h.first + h.thead;
  h.rows.forEach((rh, i) => {
    const cur = pages[pages.length - 1]!;
    if (used + rh > content && cur.length) { pages.push([]); used = h.other + h.thead; }
    pages[pages.length - 1]!.push(i);
    used += rh;
  });
  // The totals row and the notes stay with at least one row on the last page.
  const tail = h.foot + h.notes;
  if (used + tail > content) {
    const last = pages[pages.length - 1]!;
    pages.push(last.length > 1 ? [last.pop()!] : []);
  }
  return pages;
}

function Pages({ d, onReady }: { d: ReportPrintData; onReady(): void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<number[][] | null>(null);
  const land = orientation(d) === "landscape";
  useLayoutEffect(() => {
    if (pages) { onReady(); return; }
    const m = ref.current!;
    const q = (s: string) => m.querySelector<HTMLElement>(s)?.getBoundingClientRect().height ?? 0;
    const rows = [...m.querySelectorAll<HTMLElement>("tbody.rpp-measure > tr")].map((x) => x.getBoundingClientRect().height);
    setPages(paginate({ first: q(".rpp-m-first"), other: q(".rpp-m-other"), thead: q("thead"), foot: q("tfoot"), notes: q(".rpp-notes"), pageFoot: q(".rpp-pfoot"), rows }, land ? 210 : 297));
  });
  if (!pages) {
    // Measuring pass: the same widths and fonts as the pages, out of sight.
    return (
      <div ref={ref} className={`rpp-measure-box rpp-sheet ${land ? "land" : ""}`} aria-hidden>
        <div className="rpp-m-first"><Head d={d} first /></div>
        <div className="rpp-m-other"><Head d={d} first={false} /></div>
        <table className="rpp-tbl"><thead><THead cols={d.cols} /></thead>
          <tbody className="rpp-measure">{d.rows.map((r, i) => <Row key={i} r={r} cols={d.cols} />)}</tbody><Foot d={d} /></table>
        <Notes d={d} />
        <footer className="rpp-pfoot"><span>{d.text.page(1, 1)}</span></footer>
      </div>
    );
  }
  return (
    <>{pages.map((idx, p) => {
      const last = p === pages.length - 1;
      return (
        <section key={p} className={`rpp-page rpp-sheet ${land ? "land" : ""}`} data-page={p + 1}>
          <Head d={d} first={p === 0} />
          <table className="rpp-tbl">
            <thead><THead cols={d.cols} /></thead>
            <tbody>{idx.map((i) => <Row key={i} r={d.rows[i]!} cols={d.cols} />)}
              {!d.rows.length && <tr><td colSpan={d.cols.length} className="rpp-empty">{d.text.noRows}</td></tr>}</tbody>
            {last && <Foot d={d} />}
          </table>
          {last && <Notes d={d} />}
          <footer className="rpp-pfoot"><span>{d.text.printedBy}</span><span className="num">{d.text.page(p + 1, pages.length)}</span></footer>
        </section>
      );
    })}</>
  );
}

let active: (() => void) | null = null;

/**
 * Prints the report through the browser's print dialog (or "Save as PDF"), with only the report's pages on the paper.
 * Resolves once the dialog closes. The screen is left as it was.
 */
export function printReport(d: ReportPrintData): Promise<void> {
  active?.();
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.className = "rprint";
    host.dir = d.lang === "ar" ? "rtl" : "ltr";
    host.lang = d.lang;
    const style = document.createElement("style");
    style.textContent = `@page { size: A4 ${orientation(d)}; margin: 0; }`;
    document.head.appendChild(style);
    document.body.appendChild(host);
    const root = createRoot(host);
    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true; active = null;
      window.removeEventListener("afterprint", cleanup);
      document.body.classList.remove("rprint-on");
      root.unmount(); host.remove(); style.remove();
      resolve();
    };
    active = cleanup;
    root.render(<Pages d={d} onReady={() => {
      document.body.classList.add("rprint-on");
      window.addEventListener("afterprint", cleanup);
      // Images (the logo) load before the dialog takes its snapshot.
      const imgs = [...host.querySelectorAll("img")].filter((i) => !i.complete);
      Promise.all(imgs.map((i) => new Promise((ok) => { i.onload = i.onerror = ok; setTimeout(ok, 1500); }))).then(() => {
        requestAnimationFrame(() => { window.print(); if (!("onafterprint" in window)) cleanup(); });
      });
    }} />);
  });
}

/**
 * The same report as a document for direct printing on this PC's A4 printer (raster pages through the print agent):
 * the header once, the column headings repeated on every page, and the page number at the foot of each page.
 */
export function reportDoc(d: ReportPrintData, logo: PrintImage | null): PrintDoc {
  const dir = d.lang === "ar" ? "rtl" : "ltr";
  // Each column's share of the width follows its longest word, so a short column (a unit) is never cut mid-word and a
  // long name gets the room to wrap between words (D-38).
  const longest = d.cols.map((_, i) => Math.max(d.cols[i]!.label.split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0),
    ...d.rows.map((r) => (r.cells[i] ?? "").split(/\s+/).reduce((m, w) => Math.max(m, w.length), 0))));
  const weights = d.cols.map((c, i) => (c.end ? 1.1 : Math.min(3, Math.max(0.9, longest[i]! / 7))));
  const cells = (texts: string[], bold?: boolean) => d.cols.map((c, i) => ({ text: texts[i] ?? "", w: weights[i]!, align: (c.end ? "end" : "start") as "end" | "start", bold }));
  const headTexts = d.cols.map((c) => (c.grp ? `${c.grp} – ${c.label}` : c.label));
  const blocks: Block[] = [
    ...(logo ? [{ t: "image", img: logo, mm: 14, widthMm: 40 } as Block] : []),
    { t: "text", text: d.org, bold: true, size: 10, align: "start" },
    { t: "text", text: d.title, bold: true, size: 13, align: "start" },
    ...d.meta.map(([k, v]): Block => ({ t: "kv", k, v, size: 8 })),
    { t: "space", mm: 2 },
    { t: "row", cells: cells(headTexts, true), size: 7.5, keep: true }, { t: "rule", keep: true }
  ];
  for (const r of d.rows) {
    if (r.kind === "section") blocks.push({ t: "text", text: r.cells[0] ?? "", bold: true, size: 9 });
    else blocks.push({ t: "row", cells: cells(r.cells, r.kind === "subtotal" || r.kind === "total"), size: 7.5 });
  }
  if (!d.rows.length) blocks.push({ t: "text", text: d.text.noRows, align: "center" });
  if (d.foot) blocks.push({ t: "rule", withPrev: true }, { t: "row", cells: cells(d.foot, true), size: 7.5, withPrev: true });
  for (const n of d.notes ?? []) blocks.push({ t: "text", text: n, size: 8 });
  return {
    dir, blocks,
    // Pages after the first start with the column headings again.
    pageHead: [{ t: "row", cells: cells(headTexts, true), size: 7.5 }, { t: "rule" }],
    pageFoot: (i, n) => `${d.text.printedBy} · ${d.text.page(i, n)}`
  };
}
